import * as Headless from '@headlessui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListFilter } from 'lucide-react';
import { useThemeContext } from '../../../utils/providers/theme-context';
import { DataGridColumn } from './data-grid-types';

interface ColumnFilterProps<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  column: any;
  columnDef: DataGridColumn<T>;
  manualFiltering: boolean;
  value: string;
  onValueChange: (val: string, commit?: boolean) => void;
  showInline?: boolean;
  buttonOpacity?: number;
}

export function ColumnFilter<T>({
  columnDef,
  value,
  onValueChange,
  showInline = false,
  buttonOpacity
}: ColumnFilterProps<T>) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();
  const navigate = useNavigate();

  const themeText = getColour('normalText');
  const themeLayer = getColour('secondLayer');
  const themeBorder = getColour('border');

  const filterVariant = columnDef.filterVariant || 'text';
  const isSearchable = columnDef.filterSearchable;
  const inputType =
    filterVariant === 'number'
      ? 'number'
      : filterVariant === 'date'
        ? 'date'
        : 'text';

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);

  if (showInline) {
    if (
      filterVariant === 'select' &&
      columnDef.filterOptions &&
      !isSearchable
    ) {
      return (
        <select
          value={value}
          onChange={e => onValueChange(e.target.value, true)}
          className="mt-1 w-full text-xs rounded-2xl px-2 py-1 border"
          style={{
            borderColor:
              columnDef.filterVariant === 'select' ? themeBorder : undefined,
            backgroundColor: themeLayer,
            color: themeText
          }}
          onClick={e => e.stopPropagation()}
        >
          <option value="">{t('all')}</option>
          {columnDef.filterOptions.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (filterVariant === 'date-range') {
      const urlParams = new URLSearchParams(window.location.search);
      const fromDate = urlParams.get('from') || '';
      const toDate = urlParams.get('to') || '';
      const hasDateFilter = fromDate || toDate;

      return (
        <span
          className="mt-1 inline-block text-xs px-2 py-1"
          style={{
            color: hasDateFilter ? getColour('activeText') : themeText,
            opacity: hasDateFilter ? 1 : 0.7
          }}
        >
          {hasDateFilter
            ? `${fromDate || '...'} - ${toDate || '...'}`
            : t('filter')}
        </span>
      );
    }

    return (
      <input
        type={inputType}
        value={value}
        onChange={e => onValueChange(e.target.value, false)}
        placeholder={t('filter')}
        className="mt-1 w-full text-xs rounded-2xl px-2 py-1 border"
        style={{
          borderColor: themeBorder,
          backgroundColor: themeLayer,
          color: themeText
        }}
        onClick={e => e.stopPropagation()}
      />
    );
  }

  return (
    <Headless.Menu>
      {({ close }) => (
        <>
          <Headless.MenuButton
            className="inline-flex items-center p-0.5 rounded transition-colors hover:bg-black/5 dark:hover:bg-white/8"
            style={{
              color: value
                ? getColour('activeText')
                : getColour('inactiveText'),
              opacity: buttonOpacity
            }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            title={t('filter')}
          >
            <ListFilter className="w-3.5 h-3.5" />
          </Headless.MenuButton>

          <Headless.MenuItems
            anchor="bottom"
            className="z-50 rounded-2xl shadow-pop p-3 min-w-[220px] mt-1 focus:outline-none [--anchor-gap:0.25rem] [--anchor-padding:1rem]"
            style={{
              backgroundColor: themeLayer,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: themeBorder
            }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <div
              onClick={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key === ' ') e.stopPropagation();
              }}
            >
              {filterVariant === 'select' &&
              columnDef.filterOptions &&
              !isSearchable ? (
                <select
                  value={local}
                  onChange={e => {
                    setLocal(e.target.value);
                    onValueChange(e.target.value, true);
                    close();
                  }}
                  className="w-full text-xs rounded-2xl px-2 py-2 border outline-none focus:outline-none focus:ring-1"
                  style={{
                    borderColor: themeBorder,
                    backgroundColor: themeLayer,
                    color: themeText
                  }}
                  autoFocus
                >
                  <option value="">{t('all')}</option>
                  {columnDef.filterOptions.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : filterVariant === 'date-range' ? (
                (() => {
                  const urlParams = new URLSearchParams(window.location.search);
                  const fromDate = urlParams.get('from') || '';
                  const toDate = urlParams.get('to') || '';

                  const handleDateChange = (
                    type: 'from' | 'to',
                    dateValue: string
                  ) => {
                    const url = new URL(window.location.href);
                    if (dateValue) {
                      url.searchParams.set(type, dateValue);
                    } else {
                      url.searchParams.delete(type);
                    }
                    url.searchParams.set('offset', '0');
                    navigate(url.pathname + url.search, { replace: true });
                  };

                  const handleClearDates = () => {
                    const url = new URL(window.location.href);
                    url.searchParams.delete('from');
                    url.searchParams.delete('to');
                    url.searchParams.set('offset', '0');
                    navigate(url.pathname + url.search, { replace: true });
                  };

                  return (
                    <div className="space-y-3">
                      <div>
                        <label
                          className="block text-xs font-medium mb-1"
                          style={{ color: themeText }}
                        >
                          {t('from') || 'From'}
                        </label>
                        <input
                          type="date"
                          value={fromDate}
                          onChange={e =>
                            handleDateChange('from', e.target.value)
                          }
                          className="w-full text-xs rounded-2xl px-2 py-2 border outline-none focus:outline-none focus:ring-1"
                          style={{
                            borderColor: themeBorder,
                            backgroundColor: themeLayer,
                            color: themeText
                          }}
                        />
                      </div>
                      <div>
                        <label
                          className="block text-xs font-medium mb-1"
                          style={{ color: themeText }}
                        >
                          {t('to') || 'To'}
                        </label>
                        <input
                          type="date"
                          value={toDate}
                          onChange={e => handleDateChange('to', e.target.value)}
                          className="w-full text-xs rounded-2xl px-2 py-2 border outline-none focus:outline-none focus:ring-1"
                          style={{
                            borderColor: themeBorder,
                            backgroundColor: themeLayer,
                            color: themeText
                          }}
                        />
                      </div>
                      {(fromDate || toDate) && (
                        <button
                          onClick={handleClearDates}
                          className="w-full px-2 py-1.5 text-xs rounded-2xl transition-colors"
                          style={{
                            backgroundColor: pick(
                              'rgb(254 226 226)',
                              'rgb(127 29 29)'
                            ),
                            color: pick('rgb(220 38 38)', 'rgb(252 165 165)')
                          }}
                        >
                          {t('clear-dates') || 'Clear dates'}
                        </button>
                      )}
                    </div>
                  );
                })()
              ) : (
                <input
                  ref={el => {
                    if (el) setTimeout(() => el.focus(), 0);
                  }}
                  type={inputType}
                  value={local}
                  onChange={e => {
                    const newValue = e.target.value;
                    setLocal(newValue);
                    onValueChange(newValue, false);
                  }}
                  onKeyDown={e => {
                    e.stopPropagation();
                    if (e.key === 'Enter') close();
                  }}
                  placeholder={
                    typeof columnDef.header === 'string' && columnDef.header
                      ? t('filter-column', { column: columnDef.header })
                      : t('filter')
                  }
                  className="w-full text-xs rounded-2xl px-2 py-2 border outline-none focus:outline-none focus:ring-1"
                  style={{
                    borderColor: themeBorder,
                    backgroundColor: themeLayer,
                    color: themeText
                  }}
                />
              )}
            </div>
          </Headless.MenuItems>
        </>
      )}
    </Headless.Menu>
  );
}
